import { AuthLayout, NoCommandCenterAccess } from '@/modules/auth/ui/components'
import type { LoadOrgStatus } from '@/modules/auth/use-cases'
import { Button } from '@/shared/ui/primitives'
import { useNoAccessPage } from './hooks/use-no-access-page'

export type NoAccessPageProps = {
  onLoadOrgStatus: LoadOrgStatus
}

export function NoAccessPage({ onLoadOrgStatus }: NoAccessPageProps) {
  const vm = useNoAccessPage({ onLoadOrgStatus })

  if (vm.view === 'loading') {
    return (
      <AuthLayout title="Checking your account…" subtitle="Hang tight while we look up your access.">
        <div data-testid="no-access-loading" className="flex items-center justify-center py-6">
          <span className="text-sm text-muted-foreground">Just a moment…</span>
        </div>
      </AuthLayout>
    )
  }

  if (vm.view === 'error') {
    return (
      <AuthLayout title="We couldn't load your account" subtitle={vm.errorMessage}>
        <div data-testid="no-access-error" className="flex flex-col gap-2">
          <Button type="button" onClick={vm.handleRetry} className="w-full">
            Try again
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              void vm.handleUseDifferentAccount()
            }}
            className="w-full"
          >
            Use a different account
          </Button>
        </div>
      </AuthLayout>
    )
  }

  if (vm.view === 'pageRestricted') {
    return (
      <AuthLayout
        title="You don't have access to that page"
        subtitle="Your role in this organization doesn't include it."
      >
        <div data-testid="no-access-page-restricted" className="flex flex-col gap-2">
          <Button type="button" onClick={vm.handleGoToDashboard} className="w-full">
            Go to dashboard
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              void vm.handleUseDifferentAccount()
            }}
            className="w-full"
          >
            Use a different account
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <NoCommandCenterAccess
      identity={vm.identity}
      activeOrgNames={vm.activeOrgNames}
      invitationOrgNames={vm.invitationOrgNames}
      onUseDifferentAccount={() => {
        void vm.handleUseDifferentAccount()
      }}
      onRegister={vm.handleRegister}
    />
  )
}
